<div class="admin-finances-container">
  <!-- Header -->
  <div class="header">
    <h1>Finanzas</h1>
    <p class="subtitle">Métricas financieras del negocio: cobros, pagos a workbies y ganancia</p>
  </div>

  <div *ngIf="error" class="error-card">
    <mat-icon color="warn">error</mat-icon>
    <span>{{ error }}</span>
  </div>

  <div *ngIf="loading" class="loading-container">
    <mat-spinner></mat-spinner>
    <p>Cargando datos financieros...</p>
  </div>

  <ng-container *ngIf="!loading && !error && finances">
    <!-- Estado vacío general -->
    <div *ngIf="!hasFinancesData()" class="empty-state">
      <mat-icon>insights</mat-icon>
      <p>Aún no hay movimientos financieros para mostrar</p>
    </div>

    <ng-container *ngIf="hasFinancesData()">
      <!-- ==================== Tarjetas ==================== -->
      <div class="finance-cards">
        <mat-card class="finance-card collected">
          <div class="finance-icon">
            <mat-icon>payments</mat-icon>
          </div>
          <div class="finance-content">
            <span class="finance-label">Cobrado real</span>
            <span class="finance-value">{{ formatCurrency(finances.totalCollected) }}</span>
            <span class="finance-note" *ngIf="finances.totalRefunded > 0">
              −{{ formatCurrency(finances.totalRefunded) }} en reembolsos
            </span>
          </div>
        </mat-card>

        <mat-card class="finance-card workbie-paid">
          <div class="finance-icon">
            <mat-icon>volunteer_activism</mat-icon>
          </div>
          <div class="finance-content">
            <span class="finance-label">Pagado a workbies</span>
            <span class="finance-value">{{ formatCurrency(finances.totalWorkbiePaid) }}</span>
          </div>
        </mat-card>

        <mat-card class="finance-card workbie-pending">
          <div class="finance-icon">
            <mat-icon>hourglass_top</mat-icon>
          </div>
          <div class="finance-content">
            <span class="finance-label">Por pagar</span>
            <span class="finance-value">{{ formatCurrency(finances.totalWorkbiePending) }}</span>
          </div>
        </mat-card>

        <mat-card class="finance-card profit" [class.negative]="finances.grossProfit < 0">
          <div class="finance-icon">
            <mat-icon>{{ finances.grossProfit >= 0 ? 'trending_up' : 'trending_down' }}</mat-icon>
          </div>
          <div class="finance-content">
            <span class="finance-label">Ganancia</span>
            <span class="finance-value">{{ formatCurrency(finances.grossProfit) }}</span>
          </div>
        </mat-card>

        <mat-card class="finance-card avg-ticket">
          <div class="finance-icon">
            <mat-icon>local_offer</mat-icon>
          </div>
          <div class="finance-content">
            <span class="finance-label">Ticket promedio</span>
            <span class="finance-value">{{ formatCurrency(finances.avgTicket) }}</span>
          </div>
        </mat-card>

        <mat-card class="finance-card jobs-count">
          <div class="finance-icon">
            <mat-icon>work_outline</mat-icon>
          </div>
          <div class="finance-content">
            <span class="finance-label"># Trabajos</span>
            <span class="finance-value">{{ finances.jobsCount }}</span>
          </div>
        </mat-card>
      </div>

      <p class="historic-caption">
        <mat-icon>info</mat-icon>
        Cobrado, pagado a workbies, por pagar y ganancia son totales <strong>históricos</strong> (todo el tiempo).
        El ticket promedio y el número de trabajos corresponden al rango de fechas filtrado abajo, al igual que el desglose por categoría y por método de pago.
      </p>

      <!-- ==================== Filtro de rango de fechas ==================== -->
      <div class="filter-bar">
        <mat-form-field appearance="outline" class="filter-field">
          <mat-label>Desde</mat-label>
          <input matInput [matDatepicker]="fromPicker" [(ngModel)]="filterFrom">
          <mat-datepicker-toggle matIconSuffix [for]="fromPicker"></mat-datepicker-toggle>
          <mat-datepicker #fromPicker></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline" class="filter-field">
          <mat-label>Hasta</mat-label>
          <input matInput [matDatepicker]="toPicker" [(ngModel)]="filterTo">
          <mat-datepicker-toggle matIconSuffix [for]="toPicker"></mat-datepicker-toggle>
          <mat-datepicker #toPicker></mat-datepicker>
        </mat-form-field>

        <button mat-raised-button color="primary" (click)="applyFilter()">
          <mat-icon>filter_alt</mat-icon>
          Filtrar
        </button>

        <button mat-stroked-button *ngIf="hasFilter()" (click)="clearFilter()">
          <mat-icon>close</mat-icon>
          Limpiar
        </button>
      </div>

      <!-- ==================== Tabla mensual ==================== -->
      <h2 class="section-title">Últimos 6 meses</h2>
      <div class="table-container finance-table-container">
        <table class="finance-table">
          <thead>
            <tr>
              <th>Mes</th>
              <th>Cobrado</th>
              <th>Costo workbies</th>
              <th>Ganancia</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let monthItem of finances.months" [class.current-month]="isCurrentMonth(monthItem.month)">
              <td class="month-cell">
                {{ formatMonthLabel(monthItem.month) }}
                <span class="current-badge" *ngIf="isCurrentMonth(monthItem.month)">Actual</span>
              </td>
              <td>{{ formatCurrency(monthItem.collected) }}</td>
              <td>{{ formatCurrency(monthItem.workbieCost) }}</td>
              <td>
                <span [class.profit-positive]="monthItem.profit >= 0" [class.profit-negative]="monthItem.profit < 0">
                  {{ formatCurrency(monthItem.profit) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ==================== Por categoría ==================== -->
      <h2 class="section-title">Por categoría</h2>

      <div *ngIf="!hasCategoryData()" class="empty-state">
        <mat-icon>category</mat-icon>
        <p>Sin datos de categorías para el rango seleccionado</p>
      </div>

      <mat-accordion multi class="category-accordion" *ngIf="hasCategoryData()">
        <mat-expansion-panel *ngFor="let category of finances.byCategory" class="category-panel">
          <mat-expansion-panel-header>
            <mat-panel-title class="category-title">{{ category.categoryName }}</mat-panel-title>
            <mat-panel-description class="category-description">
              <span class="category-stat">{{ category.jobs }} trabajos</span>
              <span class="category-stat">{{ formatCurrency(category.collected) }}</span>
              <span class="category-stat" [class.profit-positive]="category.profit >= 0" [class.profit-negative]="category.profit < 0">
                {{ formatCurrency(category.profit) }}
              </span>
            </mat-panel-description>
          </mat-expansion-panel-header>

          <table class="finance-table subcategory-table" *ngIf="category.subcategories?.length; else noSubcategories">
            <thead>
              <tr>
                <th>Subcategoría</th>
                <th>Trabajos</th>
                <th>Cobrado</th>
                <th>Costo workbies</th>
                <th>Ganancia</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let sub of category.subcategories">
                <td>{{ sub.subcategoryName }}</td>
                <td>{{ sub.jobs }}</td>
                <td>{{ formatCurrency(sub.collected) }}</td>
                <td>{{ formatCurrency(sub.workbieCost) }}</td>
                <td>
                  <span [class.profit-positive]="sub.profit >= 0" [class.profit-negative]="sub.profit < 0">
                    {{ formatCurrency(sub.profit) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <ng-template #noSubcategories>
            <p class="empty-message">Esta categoría no tiene subcategorías con movimientos en el rango seleccionado.</p>
          </ng-template>
        </mat-expansion-panel>
      </mat-accordion>

      <!-- ==================== Por método de pago ==================== -->
      <h2 class="section-title">Por método de pago</h2>

      <div *ngIf="!hasPaymentMethodData()" class="empty-state">
        <mat-icon>payment</mat-icon>
        <p>Sin datos de métodos de pago para el rango seleccionado</p>
      </div>

      <div class="payment-method-cards" *ngIf="hasPaymentMethodData()">
        <mat-card class="payment-method-card" *ngFor="let pm of finances.byPaymentMethod">
          <div class="finance-icon">
            <mat-icon>{{ paymentMethodIcon(pm.method) }}</mat-icon>
          </div>
          <div class="finance-content">
            <span class="finance-label">{{ paymentMethodLabel(pm.method) }}</span>
            <span class="finance-value">{{ formatCurrency(pm.collected) }}</span>
            <span class="finance-note">{{ pm.jobs }} trabajos</span>
          </div>
        </mat-card>
      </div>
    </ng-container>
  </ng-container>
</div>
